/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Jinhyoung Jang (jangjh@rsupport.com)
 *
 * @description
 *
 * @created at Sat Feb 17 2024
 **/
/* eslint-disable @typescript-eslint/no-explicit-any */

import { PropsWithChildren, useEffect, useRef } from "react";
import has from "lodash-es/has";

import Ticker from "./ticker";
import { useEventHandlers, EventHandler } from "./index";
import type { Handler } from "./index";

export default function EventLoopProvider(props: PropsWithChildren<unknown>) {
	const handlers = useRef<{ [key: string]: Handler }>({});

	useEventHandlers({
		onEventLoopRegister(name, handler, indexer) {
			const _handlers = Object.keys(handlers.current).filter((name) => name.startsWith(name));
			if (_handlers.length >= 0) {
				const _name = `${name}:${_handlers.length}`;
				indexer?.(_handlers.length);
				handlers.current[_name] = handler;
			}
		},
		onEventLoopUnregister(name, index: number) {
			const _name = `${name}:${index}`;
			if (has(handlers.current, _name)) {
				delete handlers.current[_name];
			}
		}
	});

	useEffect(() => {
		const eventTick = new Ticker(100, "worker");
		eventTick.start(() => {
			while (!EventHandler.EventQueue.is_empty) {
				const data = EventHandler.EventQueue.dequeue();
				if (data && data.value) {
					const { target, message } = data.value;
					const _handlers = Object.keys(handlers.current).filter((name) => name.startsWith(target));
					_handlers.forEach((name) => handlers.current[name]?.(...message));
				}
			}
		});

		return () => {
			eventTick.stop();
		};
	}, []);

	return <>{props.children}</>;
}
